'use client'

import { useEffect, useMemo } from 'react'
import styles from './TerminalTabs.module.css'
import { useGrowwHoldings } from './hooks'
import { GrowwConnector } from '../GrowwConnector'
import { Skeleton } from "@/components/ui/skeleton"

/**
 * @uiComponent
 * @name Holdings
 * @description The Holdings component displays the user's current stock portfolio on the Groww platform.
 * It shows detailed information about each holding including quantity, average price, current price,
 * profit/loss, and portfolio summary with total value and P&L calculations.
 * @location Holdings tab in the trading terminal
 * @prop apiKey The API key for Groww connector authentication
 * @prop accessToken Optional access token for enhanced authentication
 */

/**
 * @uiWorkflow
 * @name View Portfolio Holdings on Groww
 * @description How to monitor your stock holdings and portfolio performance
 * @platform groww
 * @step 1 Navigate to the trading terminal [component: TerminalTabs] [action: navigate]
 * @step 2 Click on the "Holdings" tab to view your portfolio [component: Holdings] [action: click]
 * @step 3 Review your total portfolio value in the summary section [component: Holdings] [action: observe]
 * @step 4 Check your overall profit/loss for the entire portfolio [component: Holdings] [action: observe] [notes: Green indicates profit, red indicates loss]
 * @step 5 Examine individual stock holdings in the table below [component: Holdings] [action: scroll]
 * @step 6 Compare average purchase price with current market price [component: Holdings] [action: observe]
 * @step 7 Monitor real-time P&L changes for each holding [component: Holdings] [action: observe]
 * @step 8 Use the data to make informed trading decisions [component: Holdings] [action: analyze]
 */

/**
 * @uiWorkflow
 * @name Analyze Individual Stock Performance in Holdings
 * @description How to evaluate specific stock performance within your portfolio
 * @platform groww
 * @step 1 Open the Holdings tab in the trading terminal [component: Holdings] [action: click]
 * @step 2 Locate the specific stock in your holdings table [component: Holdings] [action: scroll]
 * @step 3 Compare the average price you paid with current market price [component: Holdings] [action: observe]
 * @step 4 Review the absolute P&L amount for that stock [component: Holdings] [action: observe]
 * @step 5 Check the percentage return on your investment [component: Holdings] [action: observe]
 * @step 6 Calculate the current value of your position [component: Holdings] [action: observe] [notes: Quantity × Current Price]
 * @step 7 Determine if you want to add more or sell based on performance [component: Holdings] [action: analyze]
 */

export default function Holdings({ apiKey, accessToken }: { apiKey: string, accessToken?: string }) {
  // Use the custom hook to manage holdings data
  const { holdings, isLoading, error, fetchHoldings } = useGrowwHoldings()

  // Initialize connector with credentials if needed
  useEffect(() => {
    if (apiKey) {
      // Create and connect the connector if not already connected
      const initializeConnector = async () => {
        const connector = new GrowwConnector(apiKey)
        await connector.connect()
      }
      
      initializeConnector()
    }
  }, [apiKey])

  // Calculate derived values using useMemo to avoid unnecessary recalculations
  const { totalPnL, totalValue } = useMemo(() => {
    const pnl = holdings.reduce((sum, holding) => sum + holding.pnl, 0)
    const value = holdings.reduce(
      (sum, holding) => sum + holding.lastPrice * parseFloat(holding.quantity),
      0
    )
    return { totalPnL: pnl, totalValue: value }
  }, [holdings])

  // Render loading state
  if (isLoading && !holdings.length) {
    return (
      <div className={styles.holdingsContainer}>
        <div className={styles.holdingsSummary}>
          <Skeleton className="h-32 w-full" />
        </div>
        <div className={styles.holdingsTable}>
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    )
  }

  if (error) return (
    <div className={styles.error}>
      {error}
      <button 
        className={styles.retryButton} 
        onClick={() => fetchHoldings()}
      >
        Retry
      </button>
    </div>
  )
  
  if (!holdings.length) return <div>No holdings found</div>

  return (
    <div className={styles.holdingsContainer}>
      {isLoading && <div className={styles.refreshIndicator}>Refreshing...</div>}
      <div className={styles.holdingsSummary}>
        <div className={styles.summaryCard}>
          <h3>Total Portfolio Value</h3>
          <p>₹{totalValue.toLocaleString()}</p>
        </div>
        <div className={styles.summaryCard}>
          <h3>Total P&L</h3>
          <p className={totalPnL >= 0 ? styles.profit : styles.loss}>
            ₹{totalPnL.toLocaleString()}
          </p>
        </div>
        {/* <button 
          className={styles.refreshButton} 
          onClick={() => fetchHoldings()}
          disabled={isLoading}
        >
          Refresh
        </button> */}
      </div>
      <div className={styles.holdingsTable}>
        <table>
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Quantity</th>
              <th>Avg Price</th>
              <th>Current Price</th>
              <th>Current Value</th>
              <th>Invested Value</th>
              <th>P&L</th>
              <th>P&L %</th>
            </tr>
          </thead>
          <tbody>
            {holdings.map(holding => {
              const quantity = parseFloat(holding.quantity)
              const currentValue = holding.lastPrice * quantity
              // Calculate P&L percentage safely to avoid division by zero
              const investedValue = holding.averagePrice * quantity
              const pnlPercentage = investedValue > 0 ? (holding.pnl / investedValue) * 100 : 0
              return (
                <tr key={holding.isin}>
                  <td>{holding.symbol}</td>
                  <td>{quantity}</td>
                  <td>₹{holding.averagePrice.toLocaleString()}</td>
                  <td>₹{holding.lastPrice.toLocaleString()}</td>
                  <td>₹{currentValue.toLocaleString()}</td>
                  <td>₹{investedValue.toLocaleString()}</td>
                  <td className={holding.pnl >= 0 ? styles.profit : styles.loss}>
                    ₹{holding.pnl.toLocaleString()}
                  </td>
                  <td className={holding.pnl >= 0 ? styles.profit : styles.loss}>
                    {pnlPercentage.toFixed(2)}%
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
